/**
 * useVqeReadiness — the readiness state behind the VQE banner, derived in one
 * place for both surfaces (the full-tab workspace banner and the compact
 * popover pip) so they cannot drift.
 *
 * "Loaded" means the schema pull has landed, not that it has tables: an empty
 * database is a finished load (`ready`, the starter canvas says it has no
 * tables). A table list that FAILED also lands as a snapshot with no tables,
 * so a no-table snapshot is only `ready` when nothing recorded a failure —
 * otherwise it is `snapshot-failed`, carrying the engine's error message and a
 * "Reload schema" action. When the tables loaded but the COLUMN step failed,
 * the tables stay usable and the state is `columns-failed` (engine message +
 * Reload schema) — column pickers are empty or incomplete until a reload
 * succeeds. `snapshot-loading` only lasts while a pull or a reload is actually
 * running.
 */
import { useCallback, useSyncExternalStore } from 'react';

import type { DatabaseEngineId } from '../../../../core/engines';
import {
  getFailedArtefacts,
  getIntrospectionFailureMessage,
  subscribeSchemaReadyStatus,
} from '../../../connections/services/language-services/introspectionTruncation';
import { getEngineForConnection } from '../../../connections/utils/connectionLookup';
import { refreshIntelliSense } from '../../../intellisense/schemaRefresh/refreshIntelliSense';
import {
  schemaRefreshKey,
  useIsSchemaRefreshing,
  useSchemaRefreshStore,
} from '../../../intellisense/schemaRefresh/schemaRefreshStore';
import type { SqlResolverService } from '../../../sql-resolver/service/resolverService';
import type { VqeReadinessState } from './VqeReadinessBanner';

export type VqeReadinessInput = {
  readonly hasConnection: boolean;
  /** A database is chosen — without one no schema pull ever starts. */
  readonly hasDatabase: boolean;
  readonly isReadOnlyMode: boolean;
  /** A schema reload for the tab's `(connection, database)` is running. */
  readonly refreshing: boolean;
  /** Table count of the landed snapshot; `undefined` while none has landed. */
  readonly tableCount: number | undefined;
  /** The table list failed to load (recorded failure, rejected pull or failed reload). */
  readonly loadFailed: boolean;
  /** The column step of the load failed (recorded failure for the `columns` artefact). */
  readonly columnsFailed: boolean;
};

export function deriveVqeReadinessState(input: VqeReadinessInput): VqeReadinessState {
  if (!input.hasConnection) return 'no-connection';
  if (input.isReadOnlyMode) return 'read-only';
  if (!input.hasDatabase) return 'no-database';
  // A snapshot with tables is usable even when the latest load failed — the
  // cache keeps serving the previous good snapshot in that case.
  if (input.tableCount !== undefined && input.tableCount > 0) {
    return input.columnsFailed && !input.refreshing ? 'columns-failed' : 'ready';
  }
  if (input.refreshing) return 'snapshot-loading';
  if (input.loadFailed) return 'snapshot-failed';
  return input.tableCount === undefined ? 'snapshot-loading' : 'ready';
}

export type VqeReadiness = {
  readonly state: VqeReadinessState;
  /** The engine's error message, for `snapshot-failed` / `columns-failed` (may be absent). */
  readonly failureMessage?: string;
  /** Re-read the schema for the tab's `(connection, database)` — the banner's "Reload schema". */
  readonly reloadSchema: () => void;
};

export type UseVqeReadinessArgs = {
  readonly connectionId: string | null | undefined;
  readonly database: string | null | undefined;
  readonly isReadOnlyMode: boolean;
  /** The surface's resolver service; `undefined` when it has none yet. */
  readonly service: SqlResolverService | undefined;
};

export function useVqeReadiness({
  connectionId,
  database,
  isReadOnlyMode,
  service,
}: UseVqeReadinessArgs): VqeReadiness {
  const connId = connectionId ?? undefined;
  const db = database ?? undefined;

  // The failure record lives outside React (introspectionTruncation.ts);
  // subscribe so a failure recorded after the snapshot landed still re-renders.
  // `null` = that artefact did not fail; a string (possibly empty) = it did.
  const subscribe = useCallback((onChange: () => void) => subscribeSchemaReadyStatus(() => onChange()), []);
  const failureOf = (artefact: 'tables' | 'columns') => (): string | null => {
    if (!connId || !db || !getFailedArtefacts(connId, db).includes(artefact)) return null;
    return getIntrospectionFailureMessage(connId, db, artefact) ?? '';
  };
  const tablesFailure = useSyncExternalStore(subscribe, failureOf('tables'));
  const columnsFailure = useSyncExternalStore(subscribe, failureOf('columns'));
  const refreshing = useIsSchemaRefreshing(connId, db);
  const refreshError = useSchemaRefreshStore((state) =>
    connId && db ? state.lastError[schemaRefreshKey(connId, db)] : undefined,
  );
  const pullError = service?.getSchemaPullError();
  const snapshot = service?.getSchemaSnapshot();

  const state = deriveVqeReadinessState({
    hasConnection: !!connId,
    hasDatabase: !!db,
    isReadOnlyMode,
    refreshing,
    tableCount: snapshot?.tables.size,
    loadFailed: tablesFailure !== null || pullError !== undefined || refreshError !== undefined,
    columnsFailed: columnsFailure !== null,
  });
  const failureMessage =
    state === 'snapshot-failed'
      ? tablesFailure || pullError || refreshError || undefined
      : state === 'columns-failed'
        ? columnsFailure || undefined
        : undefined;

  const reloadSchema = useCallback(() => {
    if (!connId || !db) return;
    const engine = getEngineForConnection(connId) as DatabaseEngineId;
    // `refreshIntelliSense` records the failure itself (store `lastError`),
    // which this hook reads; nothing more to do with the rejection here.
    refreshIntelliSense({ connectionId: connId, database: db, engine, source: 'vqe' }).catch(() => undefined);
  }, [connId, db]);

  return { state, failureMessage, reloadSchema };
}
